import React, { useEffect, useState } from 'react';
import API_URL from '../../config/api';
import { getSessionCache, setSessionCache } from '../../utils/sessionCache';
import './Hero.css';

const BANNERS_CACHE_KEY = 'homepage-banners-v20260507';
const BANNERS_CACHE_TTL_MS = 30 * 60 * 1000;

const getCachedBanners = () => {
  const cachedBanners = getSessionCache(BANNERS_CACHE_KEY, BANNERS_CACHE_TTL_MS);
  return Array.isArray(cachedBanners) ? cachedBanners : [];
};

const Hero = () => {
  const [banners, setBanners] = useState(() => getCachedBanners());
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [loading, setLoading] = useState(() => getCachedBanners().length === 0);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBannerImages = async () => {
      try {
        if (banners.length === 0) {
          setLoading(true);
        }
        setError('');

        const response = await fetch(API_URL.BANNERS);
        const result = await response.json();

        if (!result.success) {
          throw new Error(result.message || 'Failed to load banners.');
        }

        const bannerRecords = Array.isArray(result.banners)
          ? result.banners
          : Array.isArray(result.data)
            ? result.data
            : [];

        const activeBanners = bannerRecords.filter(
          (banner) => banner.isActive === true || banner.is_active === true
        );
        const resolvedBanners = (activeBanners.length > 0 ? activeBanners : bannerRecords)
          .map((banner) => ({
            id: banner.id ?? banner._id ?? banner.imageUrl ?? banner.image ?? banner.name,
            imageUrl: banner.imageUrl || '',
            alt: banner.name || 'Hari Krishna Industries banner',
          }))
          .filter((banner) => banner.imageUrl);

        setBanners(resolvedBanners);
        setSessionCache(BANNERS_CACHE_KEY, resolvedBanners);
      } catch (fetchError) {
        console.error('Error fetching banners:', fetchError);
        setError('Failed to load banner images.');
      } finally {
        setLoading(false);
      }
    };

    fetchBannerImages();
  }, []);

  useEffect(() => {
    if (banners.length <= 1) {
      return undefined;
    }

    const interval = setInterval(() => {
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentImageIndex((previousIndex) => (previousIndex + 1) % banners.length);
        setIsTransitioning(false);
      }, 320);
    }, 5200);

    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [banners.length]);

  const currentBanner = banners[currentImageIndex];

  return (
    <section className="hero" aria-label="Homepage banner">
      <div className={`hero-stage${loading ? ' is-loading' : ''}`}>
        {currentBanner?.imageUrl ? (
          <img
            src={currentBanner.imageUrl}
            alt={currentBanner.alt}
            className={`hero-banner-image${isTransitioning ? ' is-transitioning' : ''}`}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        ) : loading ? (
          <div className="hero-loading-placeholder" aria-hidden="true" />
        ) : (
          <div className="hero-fallback-copy">
            <h1>Hari Krishna Industries</h1>
            <p>{error || 'Banner artwork will appear here once it loads from the live backend.'}</p>
          </div>
        )}
        {banners.length > 1 && (
          <div className="hero-slide-dots" aria-label="Banner slide controls">
            {banners.map((banner, index) => (
              <button
                key={banner.id || `banner-${index}`}
                type="button"
                className={`hero-slide-dot${index === currentImageIndex ? ' active' : ''}`}
                onClick={() => setCurrentImageIndex(index)}
                aria-label={`Show banner ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
